import { useEffect, useRef, useState } from "react";
import { getFunnel, getQuizzes, getTenants, type Cohort, type Tenant } from "@/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth";
import { useResource } from "@/hooks/useResource";
import { TenantSelect } from "@/components/TenantSelect";
import { EmbedButton } from "@/components/EmbedButton";
import { PreviewButton } from "@/components/PreviewButton";
import { FunnelTable } from "@/components/FunnelTable";
import { DateRangePicker } from "@/components/DateRangePicker";
import { buildResponsesCsv } from "@/utils/csv";
import { isoDaysAgo, toDateWindow } from "@/utils/format";

const COHORTS: { key: Cohort; label: string }[] = [
  { key: "all", label: "All" },
  { key: "completers", label: "Completers" },
  { key: "abandoners", label: "Abandoners" },
];

export function Analytics() {
  const { status } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTenantKey = useRef(searchParams.get("tenant"));
  const initialQuizSlug = useRef(searchParams.get("quiz"));
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenant, setTenant] = useState<string>("");
  const [slug, setSlug] = useState<string>("");
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  // Report filters: last 7 days by default, whole audience.
  const [from, setFrom] = useState<string>(() => isoDaysAgo(6));
  const [to, setTo] = useState<string>(() => isoDaysAgo(0));
  const [cohort, setCohort] = useState<Cohort>("all");
  const [persona, setPersona] = useState<string | null>(null);

  // Load tenants once, honoring a ?tenant= URL param on the first load. This
  // page needs URL-param-aware selection beyond useTenants' first-item default,
  // so it keeps its own loader — but with the same StrictMode `active` guard.
  // The api client reads its own creds; this re-runs on auth `status` change,
  // never gated on creds truthiness (the Google cookie lane loads alike).
  useEffect(() => {
    let active = true;
    setTenantsError(null);
    getTenants()
      .then((r) => {
        if (!active) return; // a stale (StrictMode-doubled) run must not re-consume the ref
        setTenants(r.tenants);
        const wanted = initialTenantKey.current;
        initialTenantKey.current = null; // consume: only the first load honors the URL param
        const key =
          wanted && r.tenants.some((t) => t.key === wanted) ? wanted : r.tenants[0]?.key;
        if (key) setTenant(key);
      })
      .catch(() => active && setTenantsError("Could not load tenants."));
    return () => {
      active = false;
    };
  }, [status]);

  // Load this tenant's quizzes when tenant changes, honoring a ?quiz= URL param
  // on the first quiz load.
  const { data: quizData, error: quizzesError } = useResource(
    () => getQuizzes(tenant),
    [status, tenant],
    { enabled: Boolean(tenant) },
  );
  const quizzes = quizData?.quizzes ?? [];
  useEffect(() => {
    if (!quizData) {
      setSlug("");
      return;
    }
    const wanted = initialQuizSlug.current;
    initialQuizSlug.current = null; // consume: only the first quiz-load honors the URL param
    const next =
      wanted && quizData.quizzes.some((q) => q.slug === wanted) ? wanted : quizData.quizzes[0]?.slug;
    setSlug(next ?? "");
  }, [quizData]);

  // Reset the persona filter when the quiz changes (keys are per-quiz).
  useEffect(() => setPersona(null), [slug]);

  // Load the funnel when tenant + slug are set, re-fetching on filter change.
  const { data: report, error: funnelError } = useResource(
    () => getFunnel(tenant, slug, { ...toDateWindow(from, to), cohort, persona: persona ?? undefined }),
    [status, tenant, slug, from, to, cohort, persona],
    { enabled: Boolean(tenant && slug) },
  );

  const downloadCsv = () => {
    if (!report) return;
    const blob = new Blob([buildResponsesCsv(report)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-responses-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const error =
    tenantsError ??
    (quizzesError && "Could not load quizzes.") ??
    (funnelError && "Could not load funnel.");

  return (
    <div className="space-y-8">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Analytics</h1>
          <p className="mt-1 text-sm text-dim">Quiz funnel performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <PreviewButton slug={slug} />
          <EmbedButton slug={slug} />
        </div>
      </div>

      <div className="rise card flex flex-wrap items-end gap-5 p-4">
        <TenantSelect id="tenant" tenants={tenants} value={tenant} onChange={setTenant} />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="slug" className="label">Quiz</label>
          <select
            id="slug"
            className="select min-w-44"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          >
            {quizzes.map((q) => (
              <option key={q.slug} value={q.slug}>{q.slug}</option>
            ))}
          </select>
        </div>
        <DateRangePicker from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        <div className="flex flex-col gap-1.5">
          <span className="label">Cohort</span>
          <div role="group" aria-label="Cohort" className="flex gap-1">
            {COHORTS.map((c) => (
              <button
                key={c.key}
                type="button"
                aria-pressed={cohort === c.key}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  cohort === c.key ? "bg-ink-3 text-text" : "text-dim hover:text-text"
                }`}
                onClick={() => setCohort(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <button type="button" className="btn-outline ml-auto" onClick={downloadCsv} disabled={!report}>
          Download CSV
        </button>
      </div>

      {error && <p role="alert" className="text-coral">{error}</p>}
      {report ? (
        <FunnelTable report={report} activePersona={persona} onPersonaToggle={setPersona} />
      ) : (
        !error && <p className="text-dim">Loading…</p>
      )}
    </div>
  );
}
